import DOMPurify from 'dompurify';

const SAFE_HTML_CONFIG = {
    ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'b', 'i', 'u', 'p', 'ul', 'ol', 'li', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    SAFE_FOR_TEMPLATES: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPES_ONLY: false,
    FORCE_BODY: false
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string): string {
    if (!html || typeof html !== 'string') {
        return '';
    }

    return DOMPurify.sanitize(html, SAFE_HTML_CONFIG) as string;
}

/**
 * Sanitize plain text input by escaping HTML entities
 * Use this for user text input that should be treated as plain text
 * @param text - The text to sanitize
 * @returns Sanitized text with HTML entities escaped
 */
export function sanitizeText(text: string): string {
    if (!text || typeof text !== 'string') {
        return '';
    }

    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Sanitize URL to prevent javascript: and data: URI attacks
 * @param url - The URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
    if (!url || typeof url !== 'string') {
        return '';
    }

    const trimmedUrl = url.trim();

    // Blocklist dangerous protocols
    const dangerousProtocols = /^(?:javascript|data|vbscript|file|about):/i;
    if (dangerousProtocols.test(trimmedUrl)) {
        return '';
    }

    // Allow only http, https, mailto, and tel protocols
    const allowedProtocols = /^(?:https?|mailto|tel):/i;
    if (!allowedProtocols.test(trimmedUrl)) {
        // If no protocol, assume it's a relative URL (safe)
        if (!trimmedUrl.includes(':')) {
            return trimmedUrl;
        }
        return '';
    }

    return trimmedUrl;
}

/**
 * Sanitize user input before sending to backend
 * Strips all HTML tags and returns plain text
 * @param input - The user input to sanitize
 * @returns Plain text without HTML tags
 */
export function sanitizeUserInput(input: string): string {
    if (!input || typeof input !== 'string') {
        return '';
    }

    // Remove all HTML tags
    const strippedHtml = DOMPurify.sanitize(input, {
        ALLOWED_TAGS: [],
        KEEP_CONTENT: true
    });

    return strippedHtml.trim();
}
